<template>
  <div class="actions">
    <div class="has-tooltip arrow-right">
      <button :id="`computer-options-dropdown${tagId}`" type="button" class="btn-link">
        {{ t("CardComputerComponent.computerOptions") }}
        <svg-icon-component icon="caret-down" class="icon-xs" />
      </button>
      <div class="tooltip-area">
        <ul>
          <li v-if="actions.displayBuyNow" class="mb-half">
            <button :id="`buyNowDropDown${tagId}`" class="inline-btn-link" type="button" @click="buyNow(false)">
              {{ t("CardComputerComponent.buyNow") }}
            </button>
          </li>
          <li v-if="displayMove" class="mb-half">
            <button
              :id="`moveSubscription${tagId}`"
              class="inline-btn-link"
              type="button"
              @click="toggleMoveMySubscriptionModal('click')"
            >
              {{ t("CardComputerComponent.moveMySubscription") }}
            </button>
          </li>
          <li v-if="actions.displayReinstall" class="only-show-desktop-block mb-half">
            <button
              :id="`reinstallSafeClient${tagId}`"
              class="inline-btn-link"
              type="button"
              @click="reinstallSafeClient()"
            >
              {{ t("CardComputerComponent.reinstallCarbonite") }}
            </button>
          </li>
          <li v-if="actions.displayRenew && !showBuyFlow" class="mb-half">
            <button :id="`renewUpgrade${tagId}`" class="inline-btn-link" type="button" @click="buyNow(true)">
              <span>{{ t("CardComputerComponent.renewUpgrade") }}</span>
            </button>
          </li>
          <li v-if="actions.displayRenew && showBuyFlow" class="mb-half">
            <button :id="`renewUpgrade${tagId}`" class="inline-btn-link" type="button" @click="changePlan()">
              <span>{{ t("CardComputerComponent.changePlan") }}</span>
            </button>
          </li>
          <li v-if="actions.displayChangePlan" class="mb-half">
            <button :id="`changePlan${tagId}`" class="inline-btn-link" type="button" @click="changePlan()">
              {{ t("CardComputerComponent.changePlan") }}
            </button>
          </li>
          <li v-if="displayAddVideo" class="mb-half">
            <button :id="`addVideo${tagId}`" class="inline-btn-link" type="button" @click="addVideo()">
              {{ t("CardComputerComponent.addVideo") }}
            </button>
          </li>
          <li v-if="displayAddEncryptionKey" class="mb-half">
            <button
              :id="`addEncryptionKey${tagId}`"
              class="inline-btn-link"
              type="button"
              @click="toggleAddEncryptionKeyModal(null)"
            >
              {{ t("CardComputerComponent.addEncryptionKey") }}
            </button>
          </li>
        </ul>
      </div>
    </div>
    <MoveMySubscriptionComponent
      :show-dialog="showMoveMySubscriptionModal"
      :device="device"
      @close-modal="toggleMoveMySubscriptionModal"
      @save-modal="moveSubscription"
    />
    <DownloadProductComponent
      :show-dialog="showDownloadProductModal"
      :activation-code="device.activationCode"
      :device-id="device.deviceId"
      :install-type="typeOfInstall"
      @close-modal="toggleDownloadProductModal"
    />
    <AddEncryptionKeyComponent
      :show-dialog="showAddEncryptionKeyModal"
      :computer-id="device.deviceId"
      @close-modal="toggleAddEncryptionKeyModal"
      @save-modal="hideAddEncryptionKey()"
    />
  </div>
</template>
<script lang="ts">
import { defineComponent, PropType, ref, onBeforeMount, computed } from "vue";
import { logEvent } from "@/common/logger";
import { t } from "@/i18n";
import { IDevice } from "@/common/api/unifiedPortal/interfaces";
import { unifiedApi } from "@/common/index";
import { useNotificationsStore } from "@/stores/notifications";
import { isWindows, isMac, getOsFamily } from "@/common/browserDetect";
import { ICardComputerConfigComputerDropDownActions } from "./CardComputerConfig";
import MoveMySubscriptionComponent from "@/components/Backup/CardComputer/MoveMySubscription.vue";
import DownloadProductComponent from "@/components/shared/dialogs/DownloadProduct/DownloadProduct.vue";
import AddEncryptionKeyComponent from "@/components/Backup/CardComputer/AddEncryptionKey.vue";
import SvgIconComponent from "@/components/shared/SvgIcon/SvgIcon.vue";
import { handleApiError } from "@/common/handleApiError";
import { AxiosError } from "axios";
import { getUrl } from "@/common/getUrl";
import { FLAGS } from "@/define";

const componentName = "ComputerDropDownComponent";

export default defineComponent({
  name: componentName,
  components: {
    MoveMySubscriptionComponent,
    DownloadProductComponent,
    AddEncryptionKeyComponent,
    SvgIconComponent,
  },
  props: {
    device: {
      type: Object as PropType<IDevice>,
      required: true,
    },
    displayForcedReinstallModal: {
      type: Boolean,
      required: true,
    },
    tagId: {
      type: String,
      required: true,
    },
    actions: {
      type: Object as PropType<ICardComputerConfigComputerDropDownActions>,
      required: true,
    },
  },
  emits: ["set-installer-type", "buy-now", "change-plan"],

  setup(props, context) {
    logEvent("created", componentName, props);

    const notificationsStore = useNotificationsStore();

    const displayMove = ref<boolean>(true);
    const displayAddVideo = ref<boolean>(false);
    const displayAddEncryptionKey = ref<boolean>(false);
    const showMoveMySubscriptionModal = ref<boolean>(false);
    const showDownloadProductModal = ref<boolean>(false);
    const showAddEncryptionKeyModal = ref<boolean>(false);
    const typeOfInstall = ref<string>("");
    const showMoveSubscriptionForReinstall = ref<boolean>(false);
    const modalType = ref<string>("");
    const oldComputerOs = ref<string>("");
    const newComputerOs = ref<string>("");
    const isTrial = ref<boolean>(false);

    displayMove.value = isWindows() || isMac() ? props.actions.displayMove : false;
    displayAddVideo.value = !!props.device.canOptInVideoBackup;
    displayAddEncryptionKey.value = !props.device.hasSSLKey;

    if (props.displayForcedReinstallModal) {
      reinstallSafeClient();
    }

    onBeforeMount(async () => {
      logEvent("Before Mount", componentName);
    });

    //Function will show  Move My Subscription when new and old os are not same as well as not trial
    function checkMoveSubForReinstallRequired() {
      oldComputerOs.value = props.device.platform.toLowerCase();
      newComputerOs.value = getOsFamily().toLowerCase();
      isTrial.value = props.device.isTrial;
      modalType.value =
        oldComputerOs.value.includes(newComputerOs.value) || newComputerOs.value.includes(oldComputerOs.value)
          ? isWindows()
            ? "windowsTransfer"
            : "macTransfer"
          : isWindows()
          ? "macToWindowsTransfer"
          : "windowsToMacTransfer";
      showMoveSubscriptionForReinstall.value =
        (modalType.value === "windowsToMacTransfer" || modalType.value === "macToWindowsTransfer") && !isTrial.value;
    }
    checkMoveSubForReinstallRequired();

    function toggleMoveMySubscriptionModal(type: string | null) {
      type = type || "modal";
      logEvent(
        "toggleMoveMySubscriptionModal",
        componentName,
        `${showMoveMySubscriptionModal.value ? "Hiding" : "Showing"} via ${type}`
      );
      showMoveMySubscriptionModal.value = !showMoveMySubscriptionModal.value;
    }

    function toggleDownloadProductModal(type: string | null) {
      type = type || "modal";
      logEvent(
        "toggleDownloadProductModal",
        componentName,
        `${showDownloadProductModal.value ? "Hiding" : "Showing"} via ${type}`
      );
      showDownloadProductModal.value = !showDownloadProductModal.value;
    }

    function toggleAddEncryptionKeyModal(type: string | null) {
      type = type || "modal";
      logEvent(
        "toggleAddEncryptionKeyModal",
        componentName,
        `${showAddEncryptionKeyModal.value ? "Hiding" : "Showing"} via ${type}`
      );
      showAddEncryptionKeyModal.value = !showAddEncryptionKeyModal.value;
    }

    function moveSubscription(installType: string) {
      try {
        logEvent("MoveMySubscription modal saved", componentName, installType);

        if (installType === "Transfer" || installType === "Recover") {
          context.emit("set-installer-type", props.device, installType);
        } else if (installType === "Reinstall") {
          //Close Move subscription modal
          toggleMoveMySubscriptionModal("moveSubscription");

          //Set install type
          typeOfInstall.value = installType;
          toggleDownloadProductModal("moveSubscription");
        }
      } catch (err) {
        handleApiError(err as AxiosError);
      }
    }

    function reinstallSafeClient() {
      //Set install type
      typeOfInstall.value = "Reinstall";
      if (showMoveSubscriptionForReinstall.value) {
        toggleMoveMySubscriptionModal("moveSubscription");
      } else {
        toggleDownloadProductModal("reinstallSafeClient");
      }
    }

    function buyNow(isRenew: boolean) {
      context.emit("buy-now", isRenew);
    }

    function changePlan() {
      context.emit("change-plan");
    }

    async function addVideo() {
      try {
        await unifiedApi.optInToAutomaticVideoBackup(props.device.deviceId);
        displayAddVideo.value = false;
        notificationsStore.addNotification({
          type: "OptInToAutomaticVideoBackupSuccess",
          params: { deviceName: props.device.name },
        });
      } catch (err) {
        handleApiError(err as AxiosError);
      }
    }

    function getRecoveryDisk() {
      window.open(getUrl("MIRROR_ISO"));
    }

    function hideAddEncryptionKey() {
      displayAddEncryptionKey.value = false;
    }

    const showBuyFlow = computed(() => {
      return FLAGS.ENABLE_BUYFLOW === "true";
    });

    //Expose to template
    return {
      t,
      displayMove,
      displayAddVideo,
      displayAddEncryptionKey,
      showMoveMySubscriptionModal,
      showDownloadProductModal,
      showAddEncryptionKeyModal,
      typeOfInstall,
      toggleDownloadProductModal,
      reinstallSafeClient,
      buyNow,
      addVideo,
      getRecoveryDisk,
      toggleMoveMySubscriptionModal,
      toggleAddEncryptionKeyModal,
      moveSubscription,
      hideAddEncryptionKey,
      changePlan,
      showBuyFlow,
    };
  },
});
</script>
